import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LiveTranscriptionPanel from '../components/LiveTranscriptionPanel';

/**
 * Standalone translation page — designed to be opened in a separate browser
 * window via window.open() from the live classroom. This isolates the
 * Web Speech API microphone consumer from the Whereby iframe's mic consumer,
 * which prevents the silent failures teachers were seeing in production
 * when both shared the same tab.
 *
 * The page is intentionally minimal: header + the existing live caption
 * panel maximised. Auth state is preserved through cookies, so the teacher
 * stays logged in.
 */
export default function LiveTranslatePage() {
  const [params] = useSearchParams();
  const { user, loading } = useAuth();
  const roomId = params.get('room') || 'habla-main';
  const isTeacher = user?.role === 'admin' || user?.role === 'teacher';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f0f23] text-white/70 text-sm">
        Lade…
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f0f23] text-white text-center p-6">
        <div>
          <p className="text-lg font-semibold mb-2">Bitte zuerst anmelden</p>
          <p className="text-white/60 text-sm">Melde dich auf der Hauptseite an, dann öffne dieses Fenster erneut.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f23] p-3 flex flex-col" data-testid="live-translate-page">
      <div className="mb-2 flex items-center justify-between gap-2 flex-wrap">
        <p className="text-white/80 text-sm font-semibold">
          Habla — Live-Übersetzung · Raum: <span className="text-emerald-300">{roomId}</span>
        </p>
        <button
          onClick={() => { try { window.close(); } catch { /* noop */ } }}
          className="text-white/50 hover:text-white text-xs"
        >
          Schließen
        </button>
      </div>
      <div className="flex-1 min-h-0">
        <LiveTranscriptionPanel roomId={roomId} isTeacher={isTeacher} />
      </div>
    </div>
  );
}
